import React from 'react';
import { Modal } from '@dropbox/dig-components/modal';
import { Box, Stack } from '@dropbox/dig-foundations';
import { Link, Text } from '@dropbox/dig-components/typography';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@dropbox/dig-components/buttons';
import { LabelGroup } from '@dropbox/dig-components/combinations';
import { UIIcon } from '@dropbox/dig-icons';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import {
  CheckmarkCircleLine,
  CircleSmallFilledFill,
  WarningLine,
} from '@dropbox/dig-icons/assets';
import { strong } from 'hellospa/common/hs-intl-provider';
import styles from './api-app-confirm-publish-modal.module.css';

export type PlanState =
  | { type: 'unknown' }
  | { type: 'paid'; planName: string }
  | { type: 'unpaid' };

export type ApiUsageState =
  | { type: 'unknown' }
  | { type: 'known'; successCount: number };

const messages = defineMessages({
  featuresLink: {
    id: '',
    description:
      'link, settings page, a link to the features page of the API documentation',
    defaultMessage: 'includes the features',
  },

  featureLink: {
    id: '',
    description:
      'link, settings page, a link to the features page of the API documentation',
    defaultMessage: 'include the features',
  },

  formLink: {
    id: '',
    description:
      'link, settings page, a link to the contact sales form for API plans',
    defaultMessage: 'form',
  },

  publishModalTitle: {
    id: '',
    description:
      'title, settings page, modal to confirm publishing an app to the public',
    defaultMessage: 'Before you publish...',
  },

  unknownStateBullet: {
    id: '',
    description:
      'bullet point, settings page, modal to confirm publishing an app to the public',
    defaultMessage:
      'Make sure your app is complete and all features work as expected.',
  },

  unknownPlanBullet: {
    id: '',
    description:
      'bullet point, settings page, modal to confirm publishing an app to the public when user does not have a paid plan',
    defaultMessage:
      'Verify your paid API plan {featuresLink} used in your app.',
  },

  unknownPlanBullet2: {
    id: '',
    description:
      'bullet point, settings page, modal to confirm publishing an app to the public',
    defaultMessage:
      'Need a plan? Fill out this {formLink} to speak to a member of our sales team.',
  },

  unpaidPlanTestCallNote: {
    id: '',
    description:
      'sub-bullet point, settings page, note about completing app approval without paid plan',
    defaultMessage:
      "<strong>Note</strong>: You can still complete the app approval process prior to signing up for a paid plan, but you won't be able to make production calls until a paid plan is activated.",
  },

  publishAppButton: {
    id: '',
    description:
      'button, settings page, modal to confirm publishing an app to the public',
    defaultMessage: 'Publish app',
  },

  publishCancelButton: {
    id: '',
    description:
      'button, settings page, modal to confirm publishing an app to the public',
    defaultMessage: 'Cancel',
  },

  paidPlanBullet: {
    id: '',
    description:
      'bullet point, settings page, modal to confirm publishing an app to the public when user has a paid plan',
    defaultMessage: 'Purchase a paid API plan',
  },

  unpaidPlanBullet: {
    id: '',
    description:
      'bullet point, settings page, modal to confirm publishing an app to the public when user does not have a paid plan',
    defaultMessage: 'You are not on a paid API plan',
  },

  unpaidPlanBullet2: {
    id: '',
    description:
      'bullet point, settings page, modal to confirm publishing an app to the public when user does not have a paid plan',
    defaultMessage:
      'Your plan should {featureLink} used in your app. Fill out this {formLink} to speak to a member of our sales team.',
  },

  paidPlanSubBullet: {
    id: '',
    description:
      'sub-bullet point, settings page, modal to confirm publishing an app to the public when user has a paid plan',
    defaultMessage: 'You are on the <strong>{planName}</strong> API plan.',
  },

  apiUsageSuccessBullet: {
    id: '',
    description:
      'bullet point, settings page, modal to confirm publishing an app showing successful API usage',
    defaultMessage: 'Make a successful API call',
  },

  apiUsageSuccessSubBullet: {
    id: '',
    description:
      'sub-bullet point, settings page, modal showing count of successful API calls',
    defaultMessage:
      '<strong>{successCount}</strong> successful API {successCount, plural, one {call} other {calls}} made in the last week.',
  },

  apiUsageWarningBullet: {
    id: '',
    description:
      'bullet point, settings page, modal to confirm publishing an app with no API usage',
    defaultMessage: '<strong>No successful API calls in the last week</strong>',
  },

  apiUsageWarningExplanation: {
    id: '',
    description:
      'explanation text, settings page, modal explaining why successful API calls are important before publishing',
    defaultMessage:
      'We recommend testing your integration with at least one successful API call to ensure your app works correctly for users.',
  },
});

export function ConfirmPublishModal(
  props: React.ComponentPropsWithoutRef<typeof Modal> & {
    onConfirm: () => void;
    plan: PlanState;
    apiUsage: ApiUsageState;
  },
) {
  const intl = useIntl();
  const { plan, apiUsage } = props;
  return (
    <Modal
      {...props}
      isCentered
      withCloseButton="Close"
      aria-labelledby="publish-app-title"
    >
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title id="publish-app-title">
          {intl.formatMessage(messages.publishModalTitle)}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Box paddingBottom="Macro Small">
          <StackList gap="Micro Medium">
            <>
              {apiUsage.type === 'unknown' ? <UnknownStateBullet /> : null}
              {apiUsage.type === 'known' && apiUsage.successCount > 0 ? (
                <ApiUsageSuccessBullet successCount={apiUsage.successCount} />
              ) : null}
              {apiUsage.type === 'known' && apiUsage.successCount === 0 ? (
                <ApiUsageWarningBullet />
              ) : null}
            </>
            <>
              {plan.type === 'unknown' ? <UnknownPlanBullet /> : null}
              {plan.type === 'unpaid' ? <UnpaidPlanBullet /> : null}
              {plan.type === 'paid' ? (
                <PaidPlanBullet paidApiPlanName={plan.planName} />
              ) : null}
            </>
          </StackList>
        </Box>
      </Modal.Body>

      <Modal.Footer hasTopBorder>
        <Button variant="opacity" onClick={props.onRequestClose} autoFocus>
          {intl.formatMessage(messages.publishCancelButton)}
        </Button>
        <Button variant="primary" onClick={props.onConfirm}>
          {intl.formatMessage(messages.publishAppButton)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Bullet that informs the user to make sure their app is complete and
 * working. We display this if we don't have any information about the
 * app state.
 */
function UnknownStateBullet() {
  const intl = useIntl();

  return (
    <Bullet>
      <Text>{intl.formatMessage(messages.unknownStateBullet)}</Text>
    </Bullet>
  );
}

function UnpaidPlanBullet() {
  const intl = useIntl();
  return (
    <li>
      <LabelGroup
        align="top"
        withText={
          <Text isBold>{intl.formatMessage(messages.unpaidPlanBullet)}</Text>
        }
        withLeftAccessory={
          <Box color="Warning Base" display="flex">
            <UIIcon src={WarningLine} />
          </Box>
        }
      />
      <Stack paddingLeft="32">
        <Text tagName="p" color="subtle">
          {intl.formatMessage(messages.unpaidPlanBullet2, {
            featureLink: (
              <Link
                key="featureLink"
                href={HfReactHelper.getDropboxSignUrl('features/api')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {intl.formatMessage(messages.featureLink)}
              </Link>
            ),
            formLink: (
              <Link
                key="formLink"
                href={HfReactHelper.getDropboxSignUrl('form/api-pricing')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {intl.formatMessage(messages.formLink)}
              </Link>
            ),
          })}
        </Text>
        <Text tagName="p" color="subtle">
          {intl.formatMessage(messages.unpaidPlanTestCallNote, { strong })}
        </Text>
      </Stack>
    </li>
  );
}

/**
 * Bullet that informs the user they need to purchase a paid API plan
 * when they don't have one.
 */
function UnknownPlanBullet() {
  const intl = useIntl();
  return (
    <Bullet>
      <Stack gap="Micro Small">
        <Text>
          {intl.formatMessage(messages.unknownPlanBullet, {
            featuresLink: (
              <Link
                key="featuresLink"
                href={HfReactHelper.getDropboxSignUrl('features/api')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {intl.formatMessage(messages.featuresLink)}
              </Link>
            ),
          })}
        </Text>
        <StackList gap="Micro Small">
          <Bullet>
            <Text>
              {intl.formatMessage(messages.unknownPlanBullet2, {
                formLink: (
                  <Link
                    key="formLink"
                    href={HfReactHelper.getDropboxSignUrl('form/api-pricing')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {intl.formatMessage(messages.formLink)}
                  </Link>
                ),
              })}
            </Text>
          </Bullet>
          <Bullet>
            <Text>
              {intl.formatMessage(messages.unpaidPlanTestCallNote, {
                strong,
              })}
            </Text>
          </Bullet>
        </StackList>
      </Stack>
    </Bullet>
  );
}

/**
 * Bullet that shows a success state when the user has a paid API plan
 * and can use the API for production calls.
 */
function PaidPlanBullet({ paidApiPlanName }: { paidApiPlanName?: string }) {
  const intl = useIntl();
  return (
    <li>
      <LabelGroup
        align="top"
        withText={intl.formatMessage(messages.paidPlanBullet)}
        withSubtext={
          paidApiPlanName
            ? intl.formatMessage(messages.paidPlanSubBullet, {
                planName: paidApiPlanName,
                strong,
              })
            : null
        }
        withLeftAccessory={
          <Box color="Success Base" display="flex">
            <UIIcon src={CheckmarkCircleLine} />
          </Box>
        }
      />
    </li>
  );
}

/**
 * Bullet that shows successful API calls count (when count > 0)
 */
function ApiUsageSuccessBullet({ successCount }: { successCount: number }) {
  const intl = useIntl();
  return (
    <li>
      <LabelGroup
        align="top"
        withText={intl.formatMessage(messages.apiUsageSuccessBullet)}
        withSubtext={intl.formatMessage(messages.apiUsageSuccessSubBullet, {
          successCount: successCount.toLocaleString(),
          strong,
        })}
        withLeftAccessory={
          <Box color="Success Base" display="flex">
            <UIIcon src={CheckmarkCircleLine} />
          </Box>
        }
      />
    </li>
  );
}

/**
 * Bullet that shows warning when no API calls have been made (count = 0)
 */
function ApiUsageWarningBullet() {
  const intl = useIntl();
  return (
    <li>
      <LabelGroup
        align="top"
        withText={intl.formatMessage(messages.apiUsageWarningBullet, {
          strong,
        })}
        withLeftAccessory={
          <Box color="Warning Base" display="flex">
            <UIIcon src={WarningLine} />
          </Box>
        }
      />
      <Stack paddingLeft="32">
        <Text tagName="p" color="subtle">
          {intl.formatMessage(messages.apiUsageWarningExplanation)}
        </Text>
      </Stack>
    </li>
  );
}

function StackList(props: React.ComponentPropsWithoutRef<typeof Stack>) {
  return (
    <Stack
      tagName="ul"
      className={styles.stackList}
      paddingLeft="0"
      {...props}
    />
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className={styles.bullet}>
      <span className={styles.bulletIcon}>
        <UIIcon src={CircleSmallFilledFill} size="small" />
      </span>
      {children}
    </li>
  );
}
